import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";
import { queueVisit, uploadPhoto as uploadQueuedPhoto, stashDraftPhoto, loadDraftPhoto, clearDraftPhoto } from "../../utils/offlineVisits";
import { useAuth } from "../../App";
import { celebrate } from "../../components/CelebrationHub";

const LEAD_STATUSES = [
  { key: "hot",  icon: "", label: "Hot",  desc: "Ready to convert",   color: "#dc2626", bg: "#fee2e2" },
  { key: "warm", icon: "", label: "Warm", desc: "Interested, nurture", color: "#b45309", bg: "#fef9c3" },
  { key: "cold", icon: "", label: "Cold", desc: "Not ready yet",       color: "#1d4ed8", bg: "#dbeafe" },
];
const LEAD_STAGES = ["prospect", "proposal", "follow_up", "negotiation", "won", "lost"];

const EBEK_INSTITUTION_TYPES = [
  { value: "school",              label: "School" },
  { value: "college",             label: "College" },
  { value: "engineering_college", label: "Engineering College" },
  { value: "university",          label: "University" },
  { value: "polytechnic",         label: "Polytechnic" },
  { value: "other",               label: "Other" },
];

const EBEKMED_INSTITUTION_TYPES = [
  { value: "nursing_college",      label: "Nursing College" },
  { value: "nursing_university",   label: "Nursing University" },
  { value: "skills_center",        label: "Skills Centers" },
  { value: "hospital",             label: "Hospitals" },
  { value: "corporate_office",     label: "Corporate Office" },
  { value: "other",                label: "Others" },
];

const EBEK_INTERESTED_IN_OPTIONS = [
  { value: "ebek_lpi",     label: "EBEK LPI"     },
  { value: "bookr",        label: "Bookr"        },
  { value: "ceq",          label: "CEQ"          },
  { value: "linguaskill",  label: "Linguaskill"  },
  { value: "gla",          label: "GLA"          },
  { value: "others",       label: "Others"       },
];

const EBEKMED_INTERESTED_IN_OPTIONS = [
  { value: "inc_mandatory_modules", label: "INC mandatory Modules" },
  { value: "osce_solutions", label: "OSCE Solutions" },
  { value: "online_competency_modules", label: "Online Competency modules" },
  { value: "hands_on_training", label: "Hands-On Training" },
  { value: "competency_management_solutions", label: "Competency Management solutions" },
  { value: "others", label: "Others" },
];

const ACTION_REQUIRED_OPTIONS = [
  { value: "send_proposal",     label: "Send Proposal"        },
  { value: "meet_management",   label: "Meet with Management" },
  { value: "asked_to_revisit",  label: "Asked to Revisit"     },
  { value: "other",             label: "Other"                },
];

// Two kinds of visit, because they are two different pieces of work. Walking
// into a school and being kept at the front desk is the normal first visit —
// reps were logging those as full meetings with a blank contact name, which
// made the pipeline look like it had far more live conversations than it did.
const VISIT_PURPOSES = [
  { value: "reception",  title: "Stopped at reception",  desc: "Did not get past the front desk this time" },
  { value: "management", title: "Met the management",    desc: "Sat down with the principal or a decision maker" },
];

// How the front desk said to ask for an appointment. "Come back and ask again"
// is a real answer reps were writing into the comments box, so it gets its own
// option rather than being lost in free text.
const APPOINTMENT_CHANNELS = [
  { value: "phone",         label: "Call and ask"  },
  { value: "email",         label: "Send an email" },
  { value: "walk_in_again", label: "Come in again" },
];

// Mirrors the backend's one pipeline vocabulary (utils/pipelineStages.js).
const STAGE_LABELS = {
  reception: "Reception", appointment_fixed: "Appointment fixed", visited: "Visited",
  interested: "Interested", proposal_sent: "Proposal sent", mou_shared: "MoU shared",
  mou_signed: "MoU signed", payment_requested: "Payment requested",
  closed_won: "Won", closed_lost: "Lost",
};

const MET_DESIGNATIONS = [
  "Principal", "Correspondent", "Chairman", "Director", "Vice Principal",
  "Coordinator", "Administrator", "HOD", "Receptionist", "Other",
];

export default function AddVisit({ onDone, accent }) {
  const { user } = useAuth();
  const draftKey = `ebek_crm_visit_draft_${user?.id || "user"}`;
  const [form, setForm] = useState({
    institution_name: "", institution_type: "", city: "",
    contact_name: "", contact_phone: "",
    contact_email: "", lead_status: "warm", lead_stage: "prospect",
    comments: "", photo_url: "",
    interested_in: "", action_required: "", next_follow_up_date: "",
    student_count: "", value_per_student: "",
    // Reception stage. Blank on purpose — a rep has to say which kind of visit
    // this was before they can save it.
    visit_purpose: "", met_designation: "",
    appointment_channel: "", appointment_email: "", appointment_date: "",
    next_visit_at: "", plan_of_action: "",
  });
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [offlinePhoto, setOfflinePhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [loadingInstitutions, setLoadingInstitutions] = useState(true);
  const [institutionMode, setInstitutionMode] = useState("existing");
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [institutionSearch, setInstitutionSearch] = useState("");
  const [interestOther, setInterestOther] = useState("");
  const [actionOther, setActionOther] = useState("");
  const [visitMode, setVisitMode] = useState(null);
  const [companions, setCompanions] = useState([]);
  const [companionOptions, setCompanionOptions] = useState([]);
  const [companionSearch, setCompanionSearch] = useState("");
  const [loadingCompanions, setLoadingCompanions] = useState(false);
  const [duplicateLeads, setDuplicateLeads] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  // What the company database already knows about the institution being visited.
  // lookupMatches is the "is this the one?" prompt; profile is the answer once the
  // rep says yes, and it is what decides which fields are read-only below.
  const [lookupMatches, setLookupMatches] = useState([]);
  const [lookupDismissed, setLookupDismissed] = useState("");
  const [profile, setProfile] = useState(null);
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [visitStartedAt, setVisitStartedAt] = useState(() => new Date().toISOString());

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const atReception = form.visit_purpose === "reception";

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(draftKey) || "null");
      if (!saved?.form) return;
      setForm(current => ({ ...current, ...saved.form, photo_url: "" }));
      setInstitutionMode(saved.institutionMode || "existing");
      setSelectedLeadId(saved.selectedLeadId || "");
      setInstitutionSearch(saved.institutionSearch || "");
      setInterestOther(saved.interestOther || "");
      setActionOther(saved.actionOther || "");
      setVisitMode(saved.visitMode ?? null);
      setCompanions(Array.isArray(saved.companions) ? saved.companions : []);
      if (saved.visitStartedAt) setVisitStartedAt(saved.visitStartedAt);
      loadDraftPhoto(draftKey).then((photo) => { if (photo) setOfflinePhoto(photo); }).catch(() => {});
      toast("Draft restored. Past you had your back.");
    } catch (_) {}
  }, [draftKey]);

  useEffect(() => {
    const hasContent = [form.institution_name, form.contact_name, form.contact_phone, form.contact_email,
      form.comments, form.interested_in, form.action_required, form.next_follow_up_date, form.student_count,
      form.value_per_student, selectedLeadId].some(value => String(value || "").trim());
    if (!hasContent) return;
    setDraftSaved(false);
    const timer = window.setTimeout(() => {
      const safeForm = { ...form, photo_url: "" };
      localStorage.setItem(draftKey, JSON.stringify({ safe_version: 2, form: safeForm, institutionMode, selectedLeadId, institutionSearch, interestOther, actionOther, visitMode, companions, visitStartedAt }));
      window.dispatchEvent(new Event("crm-visit-draft-changed"));
      setDraftSaved(true);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [draftKey, form, institutionMode, selectedLeadId, institutionSearch, interestOther, actionOther, visitMode, companions, visitStartedAt]);

  const beginVisit = (mode) => {
    setVisitStartedAt(new Date().toISOString());
    setVisitMode(mode);
  };

  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    window.dispatchEvent(new Event("crm-visit-draft-changed"));
  };

  const checkForDuplicateLead = async () => {
    if (institutionMode !== "new" || form.institution_name.trim().length < 3) return;
    setCheckingDuplicate(true);
    try {
      const response = await API.get("/leads/duplicate-check", {
        params: {
          institution_name: form.institution_name.trim(),
          contact_email: form.contact_email.trim() || undefined,
          contact_phone: form.contact_phone.trim() || undefined,
          city: form.city.trim() || undefined,
        },
      });
      setDuplicateLeads(Array.isArray(response.data) ? response.data : []);
    } catch {
      setDuplicateLeads([]);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // As the rep types a new institution's name, look for it in the company
  // database so they are asked before a second record is made for the same school.
  useEffect(() => {
    const typed = form.institution_name.trim();
    if (institutionMode !== "new" || profile || typed.length < 3 || typed.toLowerCase() === lookupDismissed) {
      setLookupMatches([]);
      return undefined;
    }
    let alive = true;
    const timer = window.setTimeout(async () => {
      try {
        const res = await API.get("/leads/institution-lookup", { params: { name: typed } });
        if (alive) setLookupMatches(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (alive) setLookupMatches([]);
      }
    }, 450);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [form.institution_name, institutionMode, profile, lookupDismissed]);

  // The phone box means the desk number at reception and the contact's number
  // otherwise, so what the database fills in depends on the visit type.
  const profilePhone = (p, reception) => (reception ? p?.reception_phone : (p?.contact_phone || p?.reception_phone)) || "";
  useEffect(() => {
    const value = profilePhone(profile, atReception);
    if (value) setForm(f => ({ ...f, contact_phone: value }));
  }, [profile, atReception]);

  // A field is read-only once the database already holds a value for it. Whatever
  // the database is missing stays open for the rep to fill in.
  const dbHas = (key) => {
    if (!profile) return false;
    if (key === "contact_phone") return !!profilePhone(profile, atReception) && (atReception || !contactUnlocked);
    if (key === "contact_name" || key === "contact_email") return !!profile[key] && !contactUnlocked;
    return !!profile[key];
  };
  const lockedStyle = { background: "#f1f5f9", color: "#475569" };
  const fromDb = <span style={{ marginLeft: 6, color: "#15803d", fontWeight: 700, textTransform: "none", letterSpacing: 0 }}>✓ saved</span>;

  const useInstitutionProfile = (match) => {
    // Keep the rep's own lead attached, so this visit updates it rather than
    // opening a second one.
    if (match.my_lead_id && institutions.some(i => Number(i.lead_id) === Number(match.my_lead_id))) {
      selectExistingInstitution(match.my_lead_id, { enrich: false });
    } else {
      setSelectedLeadId("");
    }
    setForm(f => ({
      ...f,
      institution_name: match.institution_name,
      institution_type: match.institution_type || f.institution_type,
      city: match.city || f.city,
      contact_name: match.contact_name || f.contact_name,
      contact_email: match.contact_email || f.contact_email,
    }));
    setProfile(match);
    setContactUnlocked(false);
    setLookupMatches([]);
  };

  const clearInstitutionProfile = () => {
    setProfile(null);
    setContactUnlocked(false);
    setSelectedLeadId("");
    setLookupDismissed(form.institution_name.trim().toLowerCase());
    setForm(f => ({ ...f, institution_type: "", city: "", contact_name: "", contact_phone: "", contact_email: "" }));
  };

  const chooseCompanionMode = async () => {
    beginVisit("companion");
    if (companionOptions.length) return;
    setLoadingCompanions(true);
    try {
      const res = await API.get("/users/visit-companions");
      setCompanionOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load employees");
    } finally {
      setLoadingCompanions(false);
    }
  };

  const toggleCompanion = (id) => {
    setCompanions(current => {
      if (current.includes(id)) return current.filter(value => value !== id);
      if (current.length >= 2) {
        toast.error("You can select a maximum of 2 companions");
        return current;
      }
      return [...current, id];
    });
  };

  useEffect(() => {
    let alive = true;
    API.get("/leads/institutions")
      .then(res => {
        if (!alive) return;
        setInstitutions(res.data || []);
        if (!res.data?.length) setInstitutionMode("new");
      })
      .catch(() => {
        if (alive) setInstitutionMode("new");
      })
      .finally(() => {
        if (alive) setLoadingInstitutions(false);
      });
    return () => { alive = false; };
  }, []);

  const selectedInstitution = useMemo(
    () => institutions.find(i => String(i.lead_id) === String(selectedLeadId)),
    [institutions, selectedLeadId]
  );
  const matchingInstitutions = useMemo(() => {
    const needle = institutionSearch.trim().toLowerCase();
    if (!needle) return institutions.slice(0, 12);
    return institutions.filter((institution) => [institution.institution_name, institution.city, institution.contact_name]
      .some((value) => String(value || "").toLowerCase().includes(needle))).slice(0, 12);
  }, [institutions, institutionSearch]);
  const approxRevenue = useMemo(() => {
    const students = Number(form.student_count || 0);
    const perStudent = Number(form.value_per_student || 0);
    return students > 0 && perStudent > 0 ? students * perStudent : 0;
  }, [form.student_count, form.value_per_student]);
  const institutionTypes = user?.company === "ebekmed" ? EBEKMED_INSTITUTION_TYPES : EBEK_INSTITUTION_TYPES;
  const interestedOptions = user?.company === "ebekmed" ? EBEKMED_INTERESTED_IN_OPTIONS : EBEK_INTERESTED_IN_OPTIONS;

  const selectExistingInstitution = (leadId, { enrich = true } = {}) => {
    setSelectedLeadId(leadId);
    setProfile(null);
    setContactUnlocked(false);
    const inst = institutions.find(i => String(i.lead_id) === String(leadId));
    if (!inst) return;
    setInstitutionSearch(inst.institution_name || "");
    // The rep's own lead only knows what this rep recorded. Ask the company
    // database for the rest so type, city and contacts that other visits captured
    // are filled in and locked too.
    if (enrich && inst.institution_name) {
      API.get("/leads/institution-lookup", { params: { name: inst.institution_name } })
        .then(res => {
          const match = (res.data || []).find(m => Number(m.institution_id) === Number(inst.institution_id));
          if (!match) return;
          setForm(f => ({
            ...f,
            institution_type: match.institution_type || f.institution_type,
            city: match.city || f.city,
            contact_name: match.contact_name || f.contact_name,
            contact_email: match.contact_email || f.contact_email,
          }));
          setProfile(match);
        })
        .catch(() => {});
    }
    setForm(f => ({
      ...f,
      institution_name: inst.institution_name || "",
      institution_type: inst.institution_type || f.institution_type || "",
      city: inst.city || f.city || "",
      contact_name: inst.contact_name || "",
      // A second visit to the same school should not make anyone hunt for the
      // reception number again — it was recorded the first time for exactly this.
      contact_phone: inst.contact_phone || inst.reception_phone || "",
      contact_email: inst.contact_email || "",
      lead_status: inst.lead_status || f.lead_status,
      lead_stage: inst.lead_stage || f.lead_stage,
      student_count: inst.student_count || f.student_count || "",
      value_per_student: inst.value_per_student || f.value_per_student || "",
    }));
  };

  const startNewInstitution = () => {
    setInstitutionMode("new");
    setSelectedLeadId("");
    setInstitutionSearch("");
    setProfile(null);
    setContactUnlocked(false);
    setLookupDismissed("");
    setForm(f => ({
      ...f,
      institution_name: "",
      institution_type: "",
      city: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    }));
  };

  const getLocation = () => {
    setGpsLoading(true);
    navigator.geolocation?.getCurrentPosition(
      pos => { setGps({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }); toast.success("Location captured"); setGpsLoading(false); },
      ()  => { toast.error("Could not get location"); setGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!navigator.onLine) {
      setOfflinePhoto(file);
      stashDraftPhoto(draftKey, file).catch(() => {});
      toast.success("Photo secured offline and will upload during sync");
      return;
    }
    setUploading(true);
    try {
      const photoUrl = await uploadQueuedPhoto(file);
      set("photo_url", photoUrl);
      toast.success("Photo uploaded ✓");
    } catch (err) {
      setOfflinePhoto(file);
      stashDraftPhoto(draftKey, file).catch(() => {});
      toast.success("Connection lost. Photo secured for offline sync.");
    }
    finally { setUploading(false); }
  };

  // Minimum date for follow-up = tomorrow
  const minDate = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  })();

  const handleSubmit = async () => {
    if (!form.institution_name.trim()) return toast.error("Institution name is required");
    if (!form.visit_purpose) return toast.error("Tell us how far you got — reception or management");
    // These match the backend's own checks. Catching them here means a rep sees
    // the problem before the upload rather than after it, which matters on a
    // phone at the gate of a school with one bar of signal.
    if (atReception) {
      if (!form.contact_phone.trim()) return toast.error("Note the reception or landline number before you leave");
      if (!form.appointment_channel) return toast.error("What did the desk tell you to do next?");
      if (form.appointment_channel === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.appointment_email.trim())) {
        return toast.error("Enter the email address they gave you");
      }
      if (!form.next_visit_at) return toast.error("Set the date and time you will come back");
      if (!form.plan_of_action.trim()) return toast.error("Add a plan of action — what you will do next");
      if (!form.comments.trim()) return toast.error("Add a short note on what happened at the desk");
    }
    if (form.action_required === "other" && !actionOther.trim()) return toast.error("Enter the required action");
    if (!gps) return toast.error("Capture your current GPS location before saving the visit");
    if (Number(gps.accuracy || Infinity) > 200) return toast.error("GPS accuracy is too low. Move outdoors and capture location again");
    // A photo taken offline never gets a photo_url — it sits in offlinePhoto
    // until sync uploads it (see uploadPhoto/queueVisit below). Rejecting the
    // save here would strand the rep after we just told them the photo was
    // safely queued.
    if (!form.photo_url && !offlinePhoto) return toast.error("Add a visit photo before saving");
    setSubmitting(true);
    const payload = {
        ...form,
        visit_mode: visitMode === "solo" ? "solo" : "companion",
        companion_ids: visitMode === "solo" ? [] : companions,
        interested_in: form.interested_in === "others" && interestOther.trim() ? interestOther.trim() : form.interested_in,
        action_required: form.action_required === "other" ? actionOther.trim() : form.action_required,
        lead_id: selectedLeadId || null,
        latitude:  gps?.latitude,
        longitude: gps?.longitude,
        gps_accuracy: gps?.accuracy,
        next_follow_up_date: form.next_follow_up_date || null,
        student_count: form.student_count || null,
        value_per_student: form.value_per_student || null,
        appointment_channel: atReception ? form.appointment_channel : null,
        appointment_email: atReception && form.appointment_channel === "email" ? form.appointment_email.trim() : null,
        appointment_date: atReception ? (form.appointment_date || null) : null,
        next_visit_at: form.next_visit_at || null,
        plan_of_action: atReception ? form.plan_of_action.trim() : null,
        met_designation: form.met_designation || null,
        client_sync_id: `visit-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        started_at: visitStartedAt,
        queued_at: new Date().toISOString(),
      };
    try {
      if(!navigator.onLine)throw new Error("offline");
      // A queued offline photo never got a photo_url, so posting `payload`
      // as-is would hit the backend's own "photo is required" check even
      // though the rep already saw "Photo Queued". Retry the upload now
      // that we're actually attempting to go online — if it still fails,
      // this throws before API.post and falls into the same offline-queue
      // branch below, exactly like a real connectivity failure would.
      let photoUrl = payload.photo_url;
      if (!photoUrl && offlinePhoto) {
        photoUrl = await uploadQueuedPhoto(offlinePhoto);
        set("photo_url", photoUrl); // cache it so a retried submit doesn't re-upload the same file
      }
      await API.post("/visits", { ...payload, photo_url: photoUrl });
      await clearDraftPhoto(draftKey);
      clearDraft();
      toast.success(
        atReception ? "Visit saved. Your return trip is on the calendar ✨"
        : form.next_follow_up_date ? "Visit saved. Follow-up is on the calendar ✨"
        : "Visit saved—you’re good to go ✨"
      );
      celebrate("visitComplete", { message: form.institution_name ? `${form.institution_name} visit captured` : "Institution visit captured successfully" });
      onDone();
    } catch (err) {
      if (!navigator.onLine || !err.response) {
        try {
          const count = await queueVisit(payload, offlinePhoto);
          await clearDraftPhoto(draftKey);
          clearDraft();
          toast.success(`Saved offline. ${count} visit${count === 1 ? " is" : "s are"} ready to sync.`);
          onDone();
        } catch {
          toast.error("Could not store this visit on the phone. Keep CRM open and try Save again.");
        }
      } else toast.error(err.response?.data?.error || "Failed to log visit");
    } finally { setSubmitting(false); }
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 12,
    border: "1.5px solid #e2e8f0", fontSize: 16, fontFamily: "inherit",
    outline: "none", background: "#fff", boxSizing: "border-box",
  };
  // Responsive 2-col grid — stacks to 1-col on phones <380px
  const grid2 = {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10,
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 6,
    display: "block", textTransform: "uppercase", letterSpacing: 0.5,
  };
  const cardStyle = {
    background: "#fff", border: "1px solid #e2e8f0",
    borderRadius: 16, padding: 16, marginBottom: 12,
  };

  const filteredCompanions = companionOptions.filter(employee => {
    const needle = companionSearch.trim().toLowerCase();
    return !needle || [employee.name, employee.email, employee.designation, employee.department]
      .some(value => String(value || "").toLowerCase().includes(needle));
  });

  if (visitMode === null) {
    const choiceStyle = {
      width: "100%", display: "flex", alignItems: "center", gap: 13,
      padding: 16, borderRadius: 14, border: "1.5px solid #e2e8f0",
      background: "#fff", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
    };
    return (
      <div style={{ maxWidth: 560, margin: "0 auto", paddingBottom: 24 }}>
        <div style={{ ...cardStyle, padding: 18 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#172033" }}>Starting this visit</div>
          <div style={{ color: "#64748b", fontSize: 13, margin: "5px 0 16px" }}>Are you going alone or with a companion?</div>
          <div style={{ display: "grid", gap: 10 }}>
            <button type="button" style={choiceStyle} onClick={() => beginVisit("solo")}>
              <span style={{ width: 44, height: 44, flex: "0 0 44px", borderRadius: 13, display: "grid", placeItems: "center", background: "#f1f5f9", color: "#334155", fontSize: 22 }}>●</span>
              <span><strong style={{ display: "block", color: "#172033", fontSize: 15 }}>Going alone</strong><small style={{ color: "#64748b" }}>Continue as a solo institution visit</small></span>
            </button>
            <button type="button" style={{ ...choiceStyle, borderColor: accent || "#0f9f9a", background: "#f0fdfa" }} onClick={chooseCompanionMode}>
              <span style={{ width: 44, height: 44, flex: "0 0 44px", borderRadius: 13, display: "grid", placeItems: "center", background: accent || "#0f9f9a", color: "#fff", fontSize: 20 }}>●●</span>
              <span><strong style={{ display: "block", color: "#172033", fontSize: 15 }}>Going with a companion</strong><small style={{ color: "#64748b" }}>Select a buddy from your company</small></span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (visitMode === "companion") {
    return (
      <div style={{ maxWidth: 560, margin: "0 auto", paddingBottom: 24 }}>
        <button type="button" onClick={() => { setVisitMode(null); setCompanions([]); }}
          style={{ border: 0, background: "transparent", color: "#475569", fontWeight: 700, padding: "4px 0 14px", cursor: "pointer" }}>
          &larr; Back
        </button>
        <div style={{ ...cardStyle, padding: 18 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#172033" }}>Add a companion</div>
          <div style={{ color: "#64748b", fontSize: 13, margin: "5px 0 14px" }}>
            Select up to 2 employees from {user?.company === "ebekmed" ? "Ebekmed" : "Ebek"}.
          </div>
          <input
            style={{ ...inputStyle, marginBottom: 12 }}
            value={companionSearch}
            onChange={event => setCompanionSearch(event.target.value)}
            placeholder="Search employee name"
          />
          <div style={{ maxHeight: "48vh", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
            {loadingCompanions ? (
              <div style={{ padding: 18, color: "#64748b", textAlign: "center" }}>Loading employees...</div>
            ) : filteredCompanions.length === 0 ? (
              <div style={{ padding: 18, color: "#64748b", textAlign: "center" }}>No employees found in your company.</div>
            ) : filteredCompanions.map(employee => {
              const selected = companions.includes(employee.id);
              return (
                <button key={employee.id} type="button" onClick={() => toggleCompanion(employee.id)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "12px 13px", border: 0, borderBottom: "1px solid #f1f5f9", background: selected ? "#ecfeff" : "#fff", textAlign: "left", cursor: "pointer" }}>
                  <span style={{ width: 38, height: 38, flex: "0 0 38px", display: "grid", placeItems: "center", borderRadius: 11, background: selected ? (accent || "#0f9f9a") : "#f1f5f9", color: selected ? "#fff" : "#475569", fontWeight: 800 }}>
                    {(employee.name || "E").charAt(0).toUpperCase()}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: "block", fontSize: 14, fontWeight: 750, color: "#172033", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{employee.name}</span>
                    <span style={{ display: "block", fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{employee.designation || employee.department || employee.email}</span>
                  </span>
                  <span style={{ width: 24, height: 24, borderRadius: 7, border: `2px solid ${selected ? (accent || "#0f9f9a") : "#cbd5e1"}`, background: selected ? (accent || "#0f9f9a") : "#fff", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900 }}>{selected ? "✓" : ""}</span>
                </button>
              );
            })}
          </div>
          <button type="button" disabled={!companions.length} onClick={() => setVisitMode("companion_selected")}
            style={{ width: "100%", marginTop: 14, padding: "13px 16px", border: 0, borderRadius: 12, background: companions.length ? (accent || "#0f9f9a") : "#cbd5e1", color: "#fff", fontWeight: 800, fontSize: 15, cursor: companions.length ? "pointer" : "not-allowed" }}>
            Continue with {companions.length || 0} companion{companions.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", marginBottom: 4 }}> Log Visit</div>
        <div style={{ fontSize: 13, color: "#64748b" }}>Record your field visit</div>
      </div>

      <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 12, background: "#f8fafc" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Visit group</div>
          <div style={{ marginTop: 4, fontSize: 14, fontWeight: 750, color: "#172033" }}>
            {visitMode === "solo"
              ? "Going alone"
              : `With ${companions.map(id => companionOptions.find(employee => employee.id === id)?.name).filter(Boolean).join(", ")}`}
          </div>
        </div>
        <button type="button" onClick={() => setVisitMode(visitMode === "solo" ? null : "companion")}
          style={{ border: "1px solid #cbd5e1", borderRadius: 9, background: "#fff", color: "#475569", padding: "7px 10px", fontSize: 12, fontWeight: 750, cursor: "pointer" }}>
          Change
        </button>
      </div>

      {/* Pick the institution before recording how the visit went.  This means a
          return visit begins with the company context already on screen. */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>Is this an existing or new institution? *</div>
        <div style={{ fontSize: 11, color: "#64748b", margin: "4px 0 12px" }}>
          Existing visits reuse the saved institution profile. New visits follow the normal create process.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 9, marginBottom: institutionMode === "existing" ? 12 : 0 }}>
          <button type="button" onClick={() => setInstitutionMode("existing")}
            style={{ padding: "12px", borderRadius: 11, border: `1.5px solid ${institutionMode === "existing" ? (accent || "#0f9f9a") : "#e2e8f0"}`, background: institutionMode === "existing" ? "#f0fdfa" : "#fff", color: "#172033", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
            Existing institution
          </button>
          <button type="button" onClick={startNewInstitution}
            style={{ padding: "12px", borderRadius: 11, border: `1.5px solid ${institutionMode === "new" ? (accent || "#0f9f9a") : "#e2e8f0"}`, background: institutionMode === "new" ? "#f0fdfa" : "#fff", color: "#172033", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
            New institution
          </button>
        </div>
        {institutionMode === "existing" && (
          <>
            <input
              style={inputStyle}
              value={institutionSearch}
              onChange={(event) => setInstitutionSearch(event.target.value)}
              placeholder={loadingInstitutions ? "Loading saved institutions..." : "Type a school, college or city to find it"}
              disabled={loadingInstitutions}
            />
            {!loadingInstitutions && !institutions.length && <div style={{ marginTop: 8, color: "#92400e", fontSize: 12 }}>No saved institutions are available yet. Choose New institution to add the first one.</div>}
            {matchingInstitutions.length > 0 && (
              <div style={{ maxHeight: 190, overflowY: "auto", marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 10 }}>
                {matchingInstitutions.map((institution) => {
                  const selected = String(institution.lead_id) === String(selectedLeadId);
                  return <button key={institution.lead_id} type="button" onClick={() => selectExistingInstitution(institution.lead_id)}
                    style={{ display: "block", width: "100%", padding: "10px 12px", border: 0, borderBottom: "1px solid #f1f5f9", background: selected ? "#ecfdf5" : "#fff", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                    <strong style={{ display: "block", color: "#172033", fontSize: 13 }}>{institution.institution_name}</strong>
                    <small style={{ color: "#64748b" }}>{[institution.city, institution.contact_name].filter(Boolean).join(" · ") || "Saved institution"}</small>
                  </button>;
                })}
              </div>
            )}
            {selectedInstitution && <div style={{ marginTop: 9, padding: "9px 12px", borderRadius: 10, background: "#ecfdf5", color: "#166534", fontSize: 12, lineHeight: 1.55 }}>
              <strong>Saved details loaded for {selectedInstitution.institution_name}.</strong> Continue to record what happened during this visit; missing details remain editable below.
            </div>}
          </>
        )}
        {institutionMode === "new" && <div style={{ padding: "10px 12px", borderRadius: 10, background: "#eff6ff", color: "#1d4ed8", fontSize: 12, fontWeight: 700 }}>New institution selected. Add its name and details after recording the visit outcome.</div>}
      </div>

      {/*  How far the rep got  */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>How far did you get? *</div>
        <div style={{ fontSize: 11, color: "#64748b", margin: "4px 0 12px" }}>
          Both count as a visit. Being turned away at the desk is normal work, not a bad day.
        </div>
        <div style={{ display: "grid", gap: 9 }}>
          {VISIT_PURPOSES.map(option => {
            const sel = form.visit_purpose === option.value;
            return (
              <button key={option.value} type="button" onClick={() => set("visit_purpose", option.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 12, width: "100%",
                  padding: "13px 14px", borderRadius: 13, cursor: "pointer",
                  border: `1.5px solid ${sel ? accent : "#e2e8f0"}`,
                  background: sel ? "#f0fdfa" : "#fff",
                  textAlign: "left", fontFamily: "inherit",
                }}>
                <span style={{ width: 22, height: 22, flex: "0 0 22px", borderRadius: "50%", border: `2px solid ${sel ? accent : "#cbd5e1"}`, background: sel ? accent : "#fff", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 12 }}>{sel ? "✓" : ""}</span>
                <span>
                  <strong style={{ display: "block", fontSize: 14, color: "#172033" }}>{option.title}</strong>
                  <small style={{ color: "#64748b" }}>{option.desc}</small>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/*  Institution Details  */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Institution Details</div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Institution *</label>
          {institutionMode === "existing" ? (
            <>
              <select
                style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
                value={selectedLeadId}
                onChange={e => e.target.value === "__new" ? startNewInstitution() : selectExistingInstitution(e.target.value)}
                disabled={loadingInstitutions}
              >
                <option value="">{loadingInstitutions ? "Loading institutions..." : "Select institution..."}</option>
                {institutions.map(inst => (
                  <option key={inst.lead_id} value={inst.lead_id}>{inst.institution_name}</option>
                ))}
                <option value="__new">+ Add new institution</option>
              </select>
              {selectedInstitution && (
                <div style={{ marginTop: 8, padding: "9px 12px", borderRadius: 10, background: "#f8fafc", color: "#475569", fontSize: 12, lineHeight: 1.6 }}>
                  <div>
                    {selectedInstitution.contact_name ? `Contact: ${selectedInstitution.contact_name}` : "No contact saved yet"}
                    {selectedInstitution.city ? ` - ${selectedInstitution.city}` : ""}
                  </div>
                  {/* Whatever the last rep learned at this school is shown before
                      this one walks in, which is the point of recording it. */}
                  <div style={{ marginTop: 3 }}>
                    Stage: <strong>{STAGE_LABELS[selectedInstitution.pipeline_stage] || "Visited"}</strong>
                    {selectedInstitution.reception_phone ? ` · Reception ${selectedInstitution.reception_phone}` : ""}
                  </div>
                  {selectedInstitution.next_visit_at && (
                    <div style={{ marginTop: 3, color: "#7c3aed", fontWeight: 700 }}>
                      Expected back {new Date(selectedInstitution.next_visit_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <input style={{ ...inputStyle, ...(profile ? lockedStyle : null) }} readOnly={!!profile} value={form.institution_name} onChange={e => { set("institution_name", e.target.value); setDuplicateLeads([]); }} onBlur={checkForDuplicateLead} placeholder="School / College / Organisation" />
              {profile && (
                <div style={{ marginTop: 8, padding: "9px 12px", borderRadius: 10, background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", fontSize: 12, lineHeight: 1.5 }}>
                  <strong>Details loaded from the database.</strong> Saved fields are locked; fill in only what is blank.
                  {profile.other_owner && <div style={{ marginTop: 3 }}>The lead for this institution is with {profile.other_owner}. Your visit is recorded under your name.</div>}
                  <button type="button" onClick={clearInstitutionProfile}
                    style={{ display: "block", marginTop: 4, padding: 0, border: "none", background: "none", color: "#166534", fontWeight: 800, fontSize: 12, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
                    This is a different institution
                  </button>
                </div>
              )}
              {!profile && lookupMatches.length > 0 && (
                <div role="alert" style={{ marginTop: 8, padding: "10px 12px", borderRadius: 10, border: "1px solid #5eead4", background: "#f0fdfa", color: "#134e4a", fontSize: 12, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>
                    {lookupMatches.some(m => m.exact) ? "This institution is already in the database. Use its saved details?" : "Already in the database? Pick it to use its saved details."}
                  </div>
                  {lookupMatches.map(match => (
                    <div key={match.institution_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: "1px solid #ccfbf1" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{match.institution_name}</div>
                        <div style={{ color: "#475569", fontSize: 11 }}>
                          {[(institutionTypes.find(t => t.value === match.institution_type)?.label || match.institution_type), match.city].filter(Boolean).join(" · ") || "No type or city saved"}
                          {match.my_lead_id ? " · Your lead" : match.other_owner ? ` · Lead with ${match.other_owner}` : ""}
                        </div>
                      </div>
                      <button type="button" onClick={() => useInstitutionProfile(match)}
                        style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 8, border: "none", background: accent, color: "#fff", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                        Yes, use details
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => { setLookupDismissed(form.institution_name.trim().toLowerCase()); setLookupMatches([]); }}
                    style={{ marginTop: 6, padding: 0, border: "none", background: "none", color: "#0f766e", fontWeight: 800, fontSize: 12, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
                    No, this is a new institution
                  </button>
                </div>
              )}
              {checkingDuplicate && <div style={{ marginTop:7, fontSize:11, color:"#64748b" }}>Checking for existing leads...</div>}
              {!profile && lookupMatches.length === 0 && duplicateLeads.length > 0 && (
                <div role="alert" style={{ marginTop:8, padding:"10px 12px", borderRadius:10, border:"1px solid #fbbf24", background:"#fffbeb", color:"#92400e", fontSize:11, lineHeight:1.5 }}>
                  <div style={{ fontWeight:800, marginBottom:4 }}>Possible existing lead found</div>
                  {duplicateLeads.slice(0,3).map(match => {
                    const ownedInstitution = institutions.find(item => Number(item.lead_id) === Number(match.lead_id));
                    return (
                      <div key={match.lead_id} style={{ marginTop:4 }}>
                        {match.institution_name} · Owner: {match.owner_name} · {(match.pipeline_stage || "visited").replaceAll("_", " ")}
                        {ownedInstitution && <button type="button" onClick={() => selectExistingInstitution(match.lead_id)} style={{ marginLeft:6, border:"none", background:"none", color:accent, fontWeight:800, cursor:"pointer" }}>Use existing</button>}
                      </div>
                    );
                  })}
                  {duplicateLeads.some(match => Number(match.user_id) !== Number(user?.id)) && <div style={{ marginTop:5 }}>Ask your manager to transfer the lead before creating another record.</div>}
                </div>
              )}
              {institutions.length > 0 && !profile && (
                <button
                  type="button"
                  onClick={() => setInstitutionMode("existing")}
                  style={{ marginTop: 8, background: "none", border: "none", color: accent, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Choose from existing institutions
                </button>
              )}
            </>
          )}
        </div>
        <div style={{ ...grid2, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Institution Type{dbHas("institution_type") && fromDb}</label>
            <select value={form.institution_type} onChange={e => set("institution_type", e.target.value)}
              disabled={dbHas("institution_type")}
              style={{ ...inputStyle, appearance: "none", cursor: "pointer", ...(dbHas("institution_type") ? lockedStyle : null) }}>
              <option value="">Select type</option>
              {institutionTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>City{dbHas("city") && fromDb}</label>
            <input style={{ ...inputStyle, ...(dbHas("city") ? lockedStyle : null) }} readOnly={dbHas("city")}
              value={form.city} onChange={e => set("city", e.target.value)} placeholder="e.g. Chennai" />
          </div>
        </div>
        {/* The reception number is the one thing a walk-in must come away with:
            it is how the appointment gets chased, and it belongs to the building
            rather than to whoever happened to be on the desk. */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>{atReception ? "Reception / landline number *" : "Phone"}{dbHas("contact_phone") && fromDb}</label>
          <input style={{ ...inputStyle, borderColor: atReception && !form.contact_phone.trim() ? "#fbbf24" : "#e2e8f0", ...(dbHas("contact_phone") ? lockedStyle : null) }}
            type="tel" value={form.contact_phone} readOnly={dbHas("contact_phone")}
            onChange={e => set("contact_phone", e.target.value)}
            placeholder={atReception ? "044 XXXX XXXX — the number on the desk" : "+91 XXXXX"} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>{atReception ? "Who did you speak to?" : "Contact Person"}{dbHas("contact_name") && fromDb}</label>
          <input style={{ ...inputStyle, ...(dbHas("contact_name") ? lockedStyle : null) }} readOnly={dbHas("contact_name")}
            value={form.contact_name} onChange={e => set("contact_name", e.target.value)}
            placeholder={atReception ? "Receptionist / staff name, if you got it" : "Principal / Manager name"} />
          {profile && !contactUnlocked && (profile.contact_name || (!atReception && profile.contact_phone)) && (
            <button type="button" onClick={() => { setContactUnlocked(true); setForm(f => ({ ...f, contact_name: "", contact_email: "", ...(atReception ? null : { contact_phone: "" }) })); }}
              style={{ marginTop: 6, padding: 0, border: "none", background: "none", color: accent, fontWeight: 700, fontSize: 12, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
              {atReception ? "Spoke to someone different? Enter their name" : "Met someone different? Enter their name and phone"}
            </button>
          )}
        </div>
        <div style={grid2}>
          <div>
            <label style={labelStyle}>Designation</label>
            <select value={form.met_designation} onChange={e => set("met_designation", e.target.value)}
              style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}>
              <option value="">Select</option>
              {MET_DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Email{dbHas("contact_email") && fromDb}</label>
            <input style={{ ...inputStyle, ...(dbHas("contact_email") ? lockedStyle : null) }} readOnly={dbHas("contact_email")}
              type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} placeholder="email@school.in" />
          </div>
        </div>
      </div>

      {/*  Getting the appointment — reception visits only  */}
      {atReception && (
        <div style={{ ...cardStyle, border: `1.5px solid ${accent}`, background: "#f0fdfa" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Getting the appointment *</div>
          <div style={{ fontSize: 11, color: "#64748b", margin: "4px 0 12px" }}>
            A visit that ends here with nothing agreed is a wasted trip. What did the desk tell you to do?
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>They said to</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {APPOINTMENT_CHANNELS.map(opt => {
                const sel = form.appointment_channel === opt.value;
                return (
                  <button key={opt.value} type="button" onClick={() => set("appointment_channel", opt.value)}
                    style={{
                      padding: "8px 16px", borderRadius: 20,
                      border: `1.5px solid ${sel ? accent : "#e2e8f0"}`,
                      background: sel ? accent : "#fff",
                      color: sel ? "#fff" : "#475569",
                      fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                    }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {form.appointment_channel === "email" && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email address they gave you *</label>
              <input style={inputStyle} type="email" value={form.appointment_email}
                onChange={e => set("appointment_email", e.target.value)}
                placeholder="principal@school.in" />
            </div>
          )}

          <div style={{ ...grid2, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Date they said to come</label>
              <input style={inputStyle} type="date" value={form.appointment_date}
                onChange={e => set("appointment_date", e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Your next visit *</label>
              <input style={{ ...inputStyle, borderColor: form.next_visit_at ? accent : "#fbbf24" }}
                type="datetime-local" value={form.next_visit_at}
                onChange={e => set("next_visit_at", e.target.value)} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Plan of action *</label>
            <textarea value={form.plan_of_action} onChange={e => set("plan_of_action", e.target.value)}
              placeholder="What you will do next — who you will call, what you will send, when you will be back"
              style={{ ...inputStyle, resize: "none", minHeight: 74 }} />
          </div>
        </div>
      )}

      {/*  Lead Status — a desk visit has nothing to judge warm or cold yet  */}
      {!atReception && (
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Lead Status</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          {LEAD_STATUSES.map(s => (
            <button key={s.key} onClick={() => set("lead_status", s.key)}
              style={{ flex: 1, padding: "12px 8px", border: `2px solid ${form.lead_status === s.key ? s.color : "#e2e8f0"}`, borderRadius: 12, background: form.lead_status === s.key ? s.bg : "#fff", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: form.lead_status === s.key ? s.color : "#64748b" }}>{s.label}</div>
            </button>
          ))}
        </div>
        <div>
          <label style={labelStyle}>Deal Stage</label>
          <select value={form.lead_stage} onChange={e => set("lead_stage", e.target.value)}
            style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}>
            {LEAD_STAGES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace("_"," ")}</option>)}
          </select>
        </div>
      </div>
      )}

      {/*  Interested In & Action Required  */}
      {!atReception && (
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Interest & Next Action</div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Interested In</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {interestedOptions.map(opt => {
              const sel = form.interested_in === opt.value;
              return (
                <button key={opt.value} onClick={() => set("interested_in", sel ? "" : opt.value)}
                  style={{
                    padding: "8px 16px", borderRadius: 20,
                    border: `1.5px solid ${sel ? accent : "#e2e8f0"}`,
                    background: sel ? accent : "#f8fafc",
                    color: sel ? "#fff" : "#475569",
                    fontWeight: 600, fontSize: 13, cursor: "pointer",
                    fontFamily: "inherit", transition: "all 0.2s",
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          {form.interested_in === "others" && (
            <input
              style={{ ...inputStyle, marginTop: 10 }}
              value={interestOther}
              onChange={e => setInterestOther(e.target.value)}
              placeholder="Type product / service name"
            />
          )}
        </div>

        <div>
          <label style={labelStyle}>Action Required</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {ACTION_REQUIRED_OPTIONS.map(opt => {
              const sel = form.action_required === opt.value;
              return (
                <button key={opt.value} onClick={() => set("action_required", sel ? "" : opt.value)}
                  style={{
                    padding: "8px 16px", borderRadius: 20,
                    border: `1.5px solid ${sel ? "#7c3aed" : "#e2e8f0"}`,
                    background: sel ? "#7c3aed" : "#f8fafc",
                    color: sel ? "#fff" : "#475569",
                    fontWeight: 600, fontSize: 13, cursor: "pointer",
                    fontFamily: "inherit", transition: "all 0.2s",
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          {form.action_required === "other" && (
            <input
              style={{ ...inputStyle, marginTop: 10 }}
              value={actionOther}
              onChange={e => setActionOther(e.target.value)}
              maxLength={50}
              placeholder="Enter the required action *"
              aria-label="Other action required"
            />
          )}
        </div>
      </div>
      )}

      {/*  Student count — not something a receptionist is asked  */}
      {!atReception && (
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Student Count & Revenue Estimate</div>
        <div style={{ ...grid2, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>No. of Students</label>
            <input style={inputStyle} type="number" min="0" value={form.student_count} onChange={e => set("student_count", e.target.value)} placeholder="e.g. 500" />
          </div>
          <div>
            <label style={labelStyle}>Value Per Student (₹)</label>
            <input style={inputStyle} type="number" min="0" step="0.01" value={form.value_per_student} onChange={e => set("value_per_student", e.target.value)} placeholder="e.g. 1200" />
          </div>
        </div>
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "12px 14px", color: "#15803d", fontSize: 13, fontWeight: 800 }}>
          Approx Revenue: Rs. {approxRevenue.toLocaleString("en-IN")}
        </div>
      </div>
      )}

      {/*  Comments  */}
      <div style={cardStyle}>
        <label style={labelStyle}>{atReception ? "What happened at the desk? *" : "Comments / Meeting Notes"}</label>
        <textarea value={form.comments} onChange={e => set("comments", e.target.value)}
          placeholder={atReception
            ? "Who stopped you, what they said, anything useful you picked up"
            : "What was discussed? Key insights from the meeting"}
          style={{ ...inputStyle, resize: "none", minHeight: 90 }} />
      </div>

      {/*  Follow-up Date — reception visits set a next visit instead  */}
      {!atReception && (
      <div style={{ ...cardStyle, border: `1.5px solid ${form.next_follow_up_date ? "#7c3aed" : "#e2e8f0"}`, background: form.next_follow_up_date ? "#faf5ff" : "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 18 }}></span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Schedule Follow-up</div>
            <div style={{ fontSize: 11, color: "#7c3aed" }}>Reminder will be sent 1 day before via WhatsApp</div>
          </div>
        </div>
        <input
          type="date"
          min={minDate}
          value={form.next_follow_up_date}
          onChange={e => set("next_follow_up_date", e.target.value)}
          style={{ ...inputStyle, borderColor: form.next_follow_up_date ? "#7c3aed" : "#e2e8f0" }}
        />
        {form.next_follow_up_date && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#7c3aed", fontWeight: 600 }}>
             Follow-up scheduled for {new Date(form.next_follow_up_date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
          </div>
        )}
      </div>
      )}

      {/*  Photo + GPS  */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Evidence</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={{ display: "block", padding: "14px", background: "#f8fafc", border: `1.5px dashed ${form.photo_url || offlinePhoto ? accent : "#cbd5e1"}`, borderRadius: 12, textAlign: "center", cursor: "pointer" }}>
            <input type="file" accept="image/*" capture="environment" onChange={uploadPhoto} style={{ display: "none" }} />
            <div style={{ fontSize: 24, marginBottom: 4 }}>{form.photo_url || offlinePhoto ? "" : ""}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: form.photo_url || offlinePhoto ? accent : "#64748b" }}>
              {uploading ? "Uploading" : form.photo_url ? "Photo Added" : offlinePhoto ? "Photo Queued (Offline)" : "Add Photo"}
            </div>
          </label>
          <button onClick={getLocation} disabled={gpsLoading}
            style={{ padding: "14px", background: "#f8fafc", border: `1.5px dashed ${gps ? accent : "#cbd5e1"}`, borderRadius: 12, textAlign: "center", cursor: "pointer", fontFamily: "inherit" }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{gps ? "" : ""}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: gps ? accent : "#64748b" }}>
              {gpsLoading ? "Getting" : gps ? `Location Set (±${Math.round(gps.accuracy || 0)}m)` : "Add Location *"}
            </div>
          </button>
        </div>
      </div>

      <button onClick={handleSubmit} disabled={submitting}
        style={{ width: "100%", padding: "15px", background: accent, color: "#fff", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
        {submitting ? "Saving..." : "Save Visit"}
      </button>
      {draftSaved && <div style={{ textAlign:"center", color:"#64748b", fontSize:11, marginTop:7 }}>Draft saved. Future you says thanks.</div>}
    </div>
  );
}
